import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, MapPinned, XCircle } from 'lucide-react';
import { useSiteData } from '../context/SiteDataContext';
import { checkPostal, type PostalCheck } from '../lib/travel';
import { money } from '../lib/format';

/** "We come to you" postal-code checker — instant service-area + travel-fee answer. */
export default function AreaCheck() {
  const { settings } = useSiteData();
  const [code, setCode] = useState('');
  const [result, setResult] = useState<PostalCheck | null>(null);

  if (settings.travel.mode === 'off') return null;

  const run = () => setResult(checkPostal(code, settings.travel));

  return (
    <div className="card mx-auto max-w-2xl p-6 sm:p-7">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-accent-600 to-sky-500 text-white">
          <MapPinned size={22} />
        </span>
        <div>
          <h3 className="font-display text-lg font-bold text-white">Mobile detailing — we come to you</h3>
          <p className="text-sm text-zinc-400">Enter your postal code for an instant service-area check.</p>
        </div>
      </div>

      <form
        className="mt-4 flex gap-2.5"
        onSubmit={(e) => {
          e.preventDefault();
          run();
        }}
      >
        <input
          className="input flex-1 uppercase"
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            setResult(null);
          }}
          placeholder="e.g. K2P 1L4"
          maxLength={7}
          aria-label="Postal code"
        />
        <button type="submit" className="btn-primary !px-5 !py-2.5 text-sm">
          Check
        </button>
      </form>

      {result && (
        <div className="mt-4 animate-fade-up">
          {result.status === 'ok' && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-400/30 bg-emerald-400/10 p-4">
              <p className="flex items-center gap-2.5 text-sm text-emerald-200">
                <CheckCircle2 size={18} className="shrink-0 text-emerald-400" />
                <span>
                  You're covered — {result.area} is in our {result.zoneName} service area.{' '}
                  {result.fee > 0 ? (
                    <>Travel fee: <span className="font-bold text-white">{money(result.fee)}</span> (one trip, any number of add-ons).</>
                  ) : (
                    <span className="font-bold text-white">No travel fee.</span>
                  )}
                </span>
              </p>
              <Link to={`/booking?postal=${encodeURIComponent(code)}`} className="btn-primary !px-4 !py-2 text-sm">
                Book Mobile Detail <ArrowRight size={14} />
              </Link>
            </div>
          )}
          {result.status === 'outside' && (
            <div className="flex items-start gap-2.5 rounded-xl border border-amber-400/30 bg-amber-400/10 p-4 text-sm text-amber-200">
              <XCircle size={18} className="mt-0.5 shrink-0 text-amber-400" />
              <span>
                {settings.travel.outsideMessage}
                {settings.allowDropoff && (
                  <>
                    {' '}You can also <Link to="/booking" className="font-semibold text-white underline decoration-amber-400/50 hover:decoration-white">drop off at our shop</Link>.
                  </>
                )}
              </span>
            </div>
          )}
          {result.status === 'invalid' && (
            <p className="text-sm text-amber-300">
              That doesn't look like a Canadian postal code — try the format K2P 1L4 (the first 3 characters are enough).
            </p>
          )}
        </div>
      )}
    </div>
  );
}
